import { Box, Checkbox, FormControlLabel, Grid, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotificationCount } from '../../../action/searchFilterAction';
import AHMIS, { API_URL } from '../../../api/api';
import AddAlertMessage from '../../../components/alert/Alert';
import CustomReactSelect from '../../../components/custom-react-select/CustomReactSelect';
import DeleteRegisterRecordConformation from '../../../components/delete-register-record-conformation/DeleteRegisterRecordConformation';
import CustomModal from '../../../components/modal/CustomModal';
import MulDartaaSelect from '../../../components/mul-dartaa-select/MulDartaaSelect';
import RegisterNotificationBell from '../../../components/notification/RegisterNotificationBell';
import { useNotificationAccept } from '../../../components/notification/useNotificationAccept';
import PendingPatientList from '../../../components/pending-patient-list/PendingPatientList';
import RegisterHeader from '../../../components/register-header/RegisterHeader';
import { AppMisc } from '../../../misc/appMisc';
import { ACUPUNCTURE, ACUPUNCTURE_SEVA_PRAKAAR, ERROR, NOTIFICATION_ACCEPTED, REQUIRED_FIELD, SOMETHING_WENT_WRONG, SUCCESS } from '../../../utils/constants';
import { ICD_CODE_OPTIONS, MUL_DARTA_NUMBERS_LIST } from '../../../utils/constants/forms';
import { DateUtils } from '../../../utils/dateUtils';
import { SessionStorage } from '../../../utils/storage/sessionStorage';
import AcupunctureRegister from '../../users/components/register/acupuncture-register/AcupunctureRegister';

const AcupunctureForm = () => {
  const [modalTitle, setModalTitle] = useState("अकुपञ्चर सेवा विवरण नयाँ रेकर्ड थप्नुहोस्।");
  const [showAcupuntureModal, setShowAcupuntureModal] = useState(false)
  const [modalDefaultValues, setModalDefaultValues] = useState({});
  const [totalSize, setTotalSize] = useState();
  const [acupuntureDataTable, setAcupuntureDataTable] = useState([]);
  const [mulDartaaOptions, setMulDartaaOptions] = useState([]);
  const [showAcupuntureDeleteModal, setShowAcupuntureDeleteModal] = useState(false);
  const [acupuntureDeleteId, setAcupuntureDeleteId] = useState(null);
  const { register, handleSubmit, setValue, formState: { errors }, reset, watch, control } = useForm();
  const [pendingPatientList, setPendingPatientList] = useState([])
  const { fromDate, toDate, searchParams } = useSelector((state) => state.dateReducer);
  const { rowsPerPage, pageNumber } = useSelector((state) => state.searchFilter);
  const dispatch = useDispatch();

  const { handleAccept: handleNotificationAccept, addNotificationId } = useNotificationAccept({
    reset,
    setModalOpen: setShowAcupuntureModal,
    setModalDefaultValues,
    transform: (data) => {
      if (data.palikaName) {
        data.palikaName = AppMisc.getMunicipalityName(data.palikaName);
      }
      return data;
    },
  });

  const openAcupuntureModal = () => {
    setShowAcupuntureModal(true);
  }

  const closeAcupuntureModal = () => {
    reset({
      icdCode: "",
      labTests: "",
      provisionalDiagnosis: "",
      tratementAndSuggestion: "",
      sewaType: "",
      referredIn: "",
      referredOut: "",
      genderViolence: "",
      patientFirstName: "",
      patientLastName: "",
      age: "",
      wardNumber: "",
      phoneNumber: "",
      nid: "",
      gaunOrTole: "",
    });
    setShowAcupuntureModal(false);
    setModalDefaultValues()
  }

  useEffect(() => {
    if (fromDate && toDate) {
      getAcupuntureData(fromDate, toDate, searchParams)
    }
  }, [fromDate, toDate, searchParams, pageNumber, rowsPerPage])

  const getAcupuntureData = (fromDate, toDate, searchParams, healthClientId, pageFromParam) => {
    let url = `${API_URL.acupunctureSewa}/fromDateRangeAndPage?fromDate=${fromDate}&toDate=${toDate}&pageNumber=${pageFromParam || (Number(pageNumber))}&rowSize=${rowsPerPage}`;
    if (healthClientId) {
      url = url + "&healthClientId=" + healthClientId;
    }
    if (searchParams) {
      url = url + "&searchParam=" + searchParams;
    }
    AHMIS.get(url)
      .then((response) => {
        let jsondata = response.data;
        setAcupuntureDataTable(jsondata?.data?.objectList || []);
        setTotalSize(jsondata?.data?.totalSize)
      })
      .catch((error) => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const submitAcupunctureRegister = (data) => {
    const isFromNotification = !!modalDefaultValues?.notificationId;
    if (modalDefaultValues?.id) {
      data.id = modalDefaultValues.id;
    }
    data = addNotificationId(data);
    AHMIS.post(API_URL.acupunctureSewa, data)
      .then(response => {
        if (response.data.type === SUCCESS) {
          closeAcupuntureModal();
          acupunturePendingPatient()
          getAcupuntureData(fromDate, toDate)
          if (isFromNotification) {
            dispatch(fetchNotificationCount(ACUPUNCTURE));
          }
        }
        AddAlertMessage({ type: response.data.type, message: response.data.message });
      })
      .catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const onHeaderParamChange = (fromDate, toDate, searchParam, healthClientId) => {
    getAcupuntureData(fromDate, toDate, searchParam, healthClientId, 1);
  }

  const acupuntureEdit = (id) => {
    AHMIS.get(API_URL.acupunctureSewa + "/" + id)
      .then((response) => {
        const data = response?.data?.data;
        data.icdCode = AppMisc.getLabelFromValues(ICD_CODE_OPTIONS, data.icdCode);
        setModalDefaultValues(data);
        setShowAcupuntureModal(true);
        if (data) {
          Object.keys(data).forEach((key) => setValue(key, data[key]));
        }
      })
      .catch(() => AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG }));
  };

  const closeAcupuntureDeleteModal = () => {
    setShowAcupuntureDeleteModal(false);
    setAcupuntureDeleteId(null);
  }

  const onDeleteAcupuntureRow = (id) => {
    setShowAcupuntureDeleteModal(true);
    setAcupuntureDeleteId(id);
  }

  const handleCustomReactSelectChange = (name, value) => {
    if (name === "icdCode") {
      const selectedValue = ICD_CODE_OPTIONS
        .flatMap((type) => type.options)
        .find((option) => option.label === value)?.value || value;

      setValue(name, selectedValue);
    } else {
      setValue(name, value);
    }
  };

  const deleteAcupunctureRow = (data) => {
    AHMIS.delete(API_URL.acupunctureSewa + "/" + acupuntureDeleteId + "?deletedReason=" + data.deletedReason)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setShowAcupuntureDeleteModal(false);
          setAcupuntureDeleteId(null);
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
          getAcupuntureData(fromDate, toDate)
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      })
      .catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const handleChecked = (e) => {
    setValue("genderViolence", e.target?.checked);
  };


  const attachMulDartaaOptions = () => {
    let mulDartaaOptions = [];
    AHMIS.get(API_URL.mulDartaaNumber + "?sewaType=7&fiscalYear=" + DateUtils.getCurrentFiscalYear())
      .then(response => {
        let data = response.data;
        SessionStorage.setItem(MUL_DARTA_NUMBERS_LIST, data);
        data.forEach(item => {
          mulDartaaOptions.push({ "value": item.mulDartaaNumber, "label": item.mulDartaaNumber + " (" + item.patientFirstName + " " + item.patientLastName + ")", id: item.id });
        });
        setMulDartaaOptions(mulDartaaOptions);
      })
  }

  useEffect(() => {
    attachMulDartaaOptions();
  }, [])

  useEffect(() => {
    acupunturePendingPatient()
  }, [])

  const acupunturePendingPatient = () => {
    let url = `${API_URL.pendingPatient}?sewaType=${"7"}&fiscalYear=${DateUtils.getCurrentFiscalYear()}&registerConstant=${ACUPUNCTURE}`;
    AHMIS.get(url)
      .then((response) => {
        let jsondata = response.data;
        setPendingPatientList(jsondata.data);
      })
      .catch((error) => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  };

  return (
    <>
      <Box marginTop={3}>
        <RegisterHeader
          registerTitle="अकुपञ्चर सेवा रजिष्टर"
          handleModalOpen={openAcupuntureModal}
          onHeaderParamChange={onHeaderParamChange}
          getRegisterData={getAcupuntureData}
          lazyLoading
          showSearchField
          rightElement={<RegisterNotificationBell targetRegister={ACUPUNCTURE} onAccept={handleNotificationAccept} />}
        />
      </Box>
      <CustomModal
        title={modalTitle}
        showModal={showAcupuntureModal}
        onModalSubmit={handleSubmit(submitAcupunctureRegister)}
        onModalClose={closeAcupuntureModal}
        maxWidth="lg"
        fullScreen
      >
        <Grid container spacing={2} alignItems="center">
          <MulDartaaSelect
            mulDartaaOptions={mulDartaaOptions}
            defaultValues={modalDefaultValues}
            register={register}
            handleSubmit={handleSubmit}
            setValue={setValue}
            errors={errors}
            watch={watch}
            registerConstant={ACUPUNCTURE}
          />
        </Grid>
        <Box marginTop={2}>
          <Typography variant="h6">General Examination</Typography>
        </Box>
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3} style={{ overflowY: 'auto' }}>
              <CustomReactSelect
                label="ICD Code"
                name="icdCode"
                onChange={handleCustomReactSelectChange}
                options={ICD_CODE_OPTIONS}
                defaultValue={modalDefaultValues?.icdCode}
                register={register}
                required
              />
              {errors?.icdCode && errors.icdCode?.type === "required" && <span className="error-message">{REQUIRED_FIELD}</span>}
            </Grid>
            <Grid item xs={12} sm={6} md={3} style={{ overflowY: 'auto' }}>
              <TextField
                label="निदानात्मक परीक्षण"
                size="small"
                variant="outlined"
                name="labTest"
                autoComplete="true"
                fullWidth
                defaultValue={modalDefaultValues?.labTest}
                {...register("labTest")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <TextField
                label="सम्भावित निदान"
                size="small"
                variant="outlined"
                name="provisionalDiagnosis"
                autoComplete="true"
                defaultValue={modalDefaultValues?.provisionalDiagnosis}
                fullWidth
                {...register("provisionalDiagnosis")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <TextField
                label="उपचार र सल्लाह"
                size="small"
                variant="outlined"
                name="treatmentAndAdvice"
                autoComplete="true"
                defaultValue={modalDefaultValues?.treatmentAndAdvice}
                fullWidth
                {...register("treatmentAndAdvice")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <CustomReactSelect
                label="अकुपन्चर चिकित्सासेवा प्रकार"
                name="sewaType"
                onChange={handleCustomReactSelectChange}
                options={ACUPUNCTURE_SEVA_PRAKAAR}
                defaultValue={modalDefaultValues?.sewaType}
                creatable
                register={register}
                required
              />
              {errors?.sewaType && errors.sewaType?.type === "required" && <span className="error-message">{REQUIRED_FIELD}</span>}
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <TextField
                label="प्रेषण भई आएको संस्थाको नाम"
                size="small"
                variant="outlined"
                name="referredIn"
                autoComplete="true"
                defaultValue={modalDefaultValues?.referredIn}
                fullWidth
                {...register("referredIn")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <TextField
                label="प्रेषण भई गएको संस्थाको नाम"
                size="small"
                variant="outlined"
                name="referredOut"
                autoComplete="true"
                defaultValue={modalDefaultValues?.referredOut}
                fullWidth
                {...register("referredOut")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <Tooltip title="लैङ्गिक हिंसा भए मात्र यो कोठामा चिन्ह लगाउनुहोस ।" placement="top" arrow>
                <Controller
                  name="genderViolence"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="लैङ्गिक हिंसा"
                      control={
                        <Checkbox {...field} checked={field.value} color="primary" />
                      }
                    />
                  )}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </CustomModal>
      {
        pendingPatientList.size > 0 &&
        <Box mt={2}>
          <PendingPatientList
            pendingPatientList={pendingPatientList}
            showModal={setShowAcupuntureModal}
            modalDefaultValues={setModalDefaultValues}
            setValue={setValue}
          />
        </Box>
      }
      <DeleteRegisterRecordConformation
        showDeleteModal={showAcupuntureDeleteModal}
        closeDeleteModel={closeAcupuntureDeleteModal}
        deleteRecord={deleteAcupunctureRow}
      />
      <AcupunctureRegister
        onEditRow={acupuntureEdit}
        tableData={acupuntureDataTable}
        deleteRow={onDeleteAcupuntureRow}
        totalSize={totalSize}
      />
    </>
  )
}

export default AcupunctureForm;
